'use client';

/**
 * @brisa/ui · facelift/header/HeaderFacelift
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #6 (header)
 * Figma origem: node 163:907 — reforma per 179:230 (Bruno 2026-05-14) +
 *   ajustes Bruno 2026-05-15: globe icon amarelo + dropdown idiomas
 *   configuráveis (Radix DropdownMenu, rounded-16px, bg-white, shadow-large).
 * mobile-tweaks-v2 (Bruno 2026-05-19, Figma node 218:1112): remove hambúrguer
 *   + drawer fullscreen; substitui por User button amarelo visível no mobile
 *   (entrar direto). Bottom navbar vive em componente separado BottomNavPill.
 *
 * Mantém: sticky + transparent + hide-on-scroll (F1-HDR-02..05).
 * 'use client' obrigatório (F1-HDR-01, F1-RSC-07).
 */
import * as React from 'react';
import Link from 'next/link';
import { Globe, User } from 'lucide-react';

import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SquiggleLogo } from '../brand/SquiggleLogo';
import { cn } from '@brisa/ui/lib/utils';

export interface HeaderNavItem {
  href: string;
  label: string;
  active?: boolean;
}

export interface HeaderLanguageOption {
  code: string;
  label: string;
}

export interface HeaderFaceliftProps extends React.HTMLAttributes<HTMLElement> {
  nav?: Array<HeaderNavItem>;
  /** Texto do CTA-link à direita (ex: "Seja parceiro"); null oculta. */
  ctaLink?: { href: string; label: string } | null;
  /** Conteúdo extra à direita (ex: icon-buttons custom); default: globe + user. */
  actions?: React.ReactNode;
  /** Lista de idiomas suportados pelo dropdown do globe. Default: pt-BR + es-ES. */
  languages?: HeaderLanguageOption[];
  /** Código do idioma atualmente selecionado (marca item com text-accent-yellow). */
  currentLanguage?: string;
  /** Callback acionado ao escolher um idioma no dropdown. */
  onLanguageChange?: (code: string) => void;
}

const DEFAULT_NAV: HeaderNavItem[] = [
  { href: '/', label: 'Capa', active: true },
  { href: '/clubes', label: 'Clubes' },
  { href: '/pass', label: 'Planos' },
];

const DEFAULT_CTA = { href: '/parceiros', label: 'Seja parceiro' };

const DEFAULT_LANGUAGES: HeaderLanguageOption[] = [
  { code: 'pt-BR', label: 'Português' },
  { code: 'es-ES', label: 'Español' },
];

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

export function HeaderFacelift({
  nav = DEFAULT_NAV,
  ctaLink = DEFAULT_CTA,
  actions,
  languages = DEFAULT_LANGUAGES,
  currentLanguage,
  onLanguageChange,
  className,
  ...props
}: HeaderFaceliftProps) {
  const [state, setState] = React.useState<'visible' | 'hidden'>('visible');
  const [hasFocus, setHasFocus] = React.useState(false);
  const lastScrollY = React.useRef(0);
  const headerRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (prefersReducedMotion()) return;
    function handleScroll() {
      const current = window.scrollY;
      const direction = current > lastScrollY.current ? 'down' : 'up';
      if (hasFocus) {
        setState('visible');
      } else if (direction === 'down' && current > 64) {
        setState('hidden');
      } else if (direction === 'up') {
        setState('visible');
      }
      lastScrollY.current = current;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasFocus]);

  function handleFocusIn() {
    setHasFocus(true);
    setState('visible');
  }
  function handleFocusOut(e: React.FocusEvent<HTMLElement>) {
    if (!headerRef.current?.contains(e.relatedTarget as Node | null)) {
      setHasFocus(false);
    }
  }

  return (
    <header
      ref={headerRef}
      data-state={state}
      data-slot="header"
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
      className={cn(
        'fixed top-0 z-[50] flex h-[72px] w-full justify-center md:h-[104px]',
        'bg-transparent transition-transform duration-base ease-out',
        'data-[state=visible]:translate-y-0 data-[state=hidden]:-translate-y-full',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'mx-auto flex w-full max-w-[1440px] items-center justify-between gap-4 px-4',
          'md:gap-8 md:px-8',
        )}
        data-slot="header-inner"
      >
      {/* Esquerda — logoArea */}
      <div className="flex shrink-0 items-center md:basis-[204px]" data-slot="logo-area">
        <Link href="/" aria-label="Brisa Pass — início" className="inline-flex items-center">
          <SquiggleLogo size="md" aria-label="Brisa Pass" />
        </Link>
      </div>

      {/* Centro — Nav principal (desktop) */}
      <nav
        aria-label="Navegação principal"
        className="hidden items-center gap-10 md:flex"
        data-slot="nav"
      >
        {nav.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            aria-current={item.active ? 'page' : undefined}
            className={cn(
              'relative flex flex-col items-center gap-2 font-body text-base font-semibold leading-none transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              item.active
                ? 'text-[hsl(var(--accent-yellow))]'
                : 'text-[hsl(var(--text-100))] hover:text-[hsl(var(--text-80))]',
            )}
            data-active={item.active ? 'true' : undefined}
          >
            <span>{item.label}</span>
            {item.active ? (
              <span
                aria-hidden="true"
                className="h-[2px] w-[39px] bg-[hsl(var(--accent-yellow))]"
              />
            ) : null}
          </Link>
        ))}
      </nav>

      {/* Direita — actions (desktop: cta-link + globe + user · mobile: globe + hambúrguer) */}
      <div className="flex shrink-0 items-center gap-3 md:gap-[23px]" data-slot="actions">
        {ctaLink ? (
          <Link
            href={ctaLink.href}
            className="hidden font-body text-sm font-semibold text-[hsl(var(--text-100))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:inline-flex"
          >
            {ctaLink.label}
          </Link>
        ) : null}
        {actions ?? (
          <div className="flex items-center gap-2 md:gap-4" data-slot="default-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary-pill"
                  size="icon"
                  className="bg-[hsl(var(--text-32))] text-[hsl(var(--accent-yellow))]"
                  aria-label="Trocar idioma"
                  data-slot="language-trigger"
                >
                  <Globe size={16} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-slot="language-menu">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    active={lang.code === currentLanguage}
                    onSelect={() => onLanguageChange?.(lang.code)}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="icon-circle"
              size="icon"
              aria-label="Entrar"
              data-slot="user-trigger"
              asChild
            >
              <Link href="/app/auth">
                <User size={16} aria-hidden="true" />
              </Link>
            </Button>
          </div>
        )}
      </div>
      </div>{/* /header-inner */}
    </header>
  );
}
